/**
 * AlertBellPopover — a bell button on a list row that opens that row's live
 * alerts, rendered by ActiveAlertPanel so the content matches the banner on the
 * matching profile page exactly.
 *
 * Used by the areas, residents and devices tables. `scope` is passed straight to
 * ActiveAlertPanel, which decides which facts that page already states elsewhere.
 *
 * Opens on hover AND on click, which is not redundant:
 *
 *   - Hover is for scanning. It opens after a short delay so dragging the mouse
 *     across a table does not flash a popover on every row it crosses.
 *   - Click PINS it open. The panel contains a link to the device profile, and a
 *     hover-only popover cannot be clicked into on a touch screen at all, nor
 *     reached by keyboard. Pinned survives until click-outside or Escape.
 *
 * Closing on mouse-leave is deliberately delayed, and the popover itself keeps
 * itself open while hovered, so the pointer can travel from the bell into the
 * panel to reach that link without it vanishing on the way.
 *
 * Portalled to document.body. `.ap-table-wrap` scrolls, and while `position: fixed`
 * is not clipped by overflow today, any ancestor gaining a transform would start
 * clipping it — the portal removes that whole class of failure. It also keeps the
 * panel above the row-actions menu instead of fighting it for stacking order.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ActiveAlertPanel from './ActiveAlertPanel.jsx';

/* Long enough that crossing a row does not trigger it, short enough not to feel
   broken when you do mean it. */
const OPEN_DELAY_MS = 140;
/* The travel time from bell to panel. Shorter than this and the panel closes
   under the pointer on its way in. */
const CLOSE_DELAY_MS = 220;

const PANEL_W = 340;
const GAP = 8;

const AlertBellPopover = ({ alerts, tzOffset, scope = 'area', label }) => {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const open = hovering || pinned;
  const count = alerts?.length || 0;

  /* Opening position: directly below the bell, horizontally clamped. Measured from
     the trigger every time, never cached — the row moves under a pinned panel when
     the table scrolls or the window resizes.

     Vertical placement is only provisional here. A panel holding two alerts runs
     ~650px, so any height guessed before render is wrong by enough to hang the
     panel off the bottom of the window; the layout effect below corrects it from the
     real measurement. */
  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(GAP, Math.min(r.left, window.innerWidth - PANEL_W - GAP)),
      top: r.bottom + GAP,
    });
  }, []);

  /* Correct the vertical position from the panel's actual height: below the bell
     when it fits, above when it does not, and pinned to the top edge when it fits
     neither way (the CSS max-height keeps that case bounded, so this terminates).

     Runs before paint so the panel is never seen in the provisional spot. Writing
     `pos` from an effect that also reads it converges in one pass because of the
     sub-pixel equality guard — without that guard it would loop forever. */
  useLayoutEffect(() => {
    if (!open || !pos) return;
    const t = triggerRef.current?.getBoundingClientRect();
    const h = panelRef.current?.getBoundingClientRect().height;
    if (!t || !h) return;

    const left = Math.max(GAP, Math.min(t.left, window.innerWidth - PANEL_W - GAP));
    let top = t.bottom + GAP;
    if (top + h > window.innerHeight - GAP) {
      const above = t.top - GAP - h;
      top = above >= GAP ? above : Math.max(GAP, window.innerHeight - GAP - h);
    }

    if (Math.abs(pos.top - top) > 0.5 || Math.abs(pos.left - left) > 0.5) {
      setPos({ left, top });
    }
  }, [open, pos, count]);

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };

  const scheduleOpen = () => {
    clearTimers();
    openTimer.current = setTimeout(() => { place(); setHovering(true); }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setHovering(false), CLOSE_DELAY_MS);
  };

  const togglePinned = () => {
    clearTimers();
    place();
    setPinned((p) => !p);
    setHovering(false);
  };

  /* Reposition while open rather than closing on scroll. A pinned panel that
      vanishes the moment the table scrolls is not pinned in any useful sense. */
  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  /* Only while pinned: an unpinned panel already closes on mouse-leave, and a
     document-wide listener per row would be a listener per alarming row. */
  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setPinned(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setPinned(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  useEffect(() => () => clearTimers(), []);

  /* Bail after every hook, never before — `count` goes to zero the moment the
     alert resolves, and an early return above the effects would change the hook
     count mid-life and take the table down with "Rendered fewer hooks than
     expected". */
  if (!count) return null;

  const title = label
    || `${count} active alert${count > 1 ? 's' : ''} — click to keep open`;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="pp-alarm-bell"
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={() => { place(); setHovering(true); }}
        onBlur={scheduleClose}
        onClick={togglePinned}
        aria-expanded={open}
        aria-label={title}
        title={title}
      >
        <i className="fe fe-bell" aria-hidden="true" />
      </button>

      {open && pos
        ? createPortal(
          <div
            ref={panelRef}
            className="pp-alert-pop"
            role="dialog"
            aria-label="Active alerts"
            style={{ left: `${pos.left}px`, top: `${pos.top}px`, width: `${PANEL_W}px` }}
            onMouseEnter={clearTimers}
            onMouseLeave={scheduleClose}
          >
            <ActiveAlertPanel alerts={alerts} tzOffset={tzOffset} scope={scope} variant="popover" />
          </div>,
          document.body,
        )
        : null}
    </>
  );
};

export default AlertBellPopover;
